import { readonly, ref } from 'vue';
import axios from 'axios';
import { BACKEND_BASE_URL } from '@/config/backend';
import { getStoredToken, normalizeSchoolId, resolveSchoolId, schoolConfigs } from '@/config/schools';

// The admin dashboard's school context is deliberately separate from the
// app-wide selectedSchoolId (localStorage): switching schools in the dashboard
// must not flip the whole app's school. sessionStorage keeps it per-tab.
const ADMIN_SCHOOL_STORAGE_KEY = 'adminSchoolId';

function readStoredAdminSchoolId() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = String(window.sessionStorage.getItem(ADMIN_SCHOOL_STORAGE_KEY) || '').trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(schoolConfigs, stored) ? stored : null;
  } catch {
    return null;
  }
}

const adminSchoolId = ref(readStoredAdminSchoolId() || resolveSchoolId());

export function setAdminSchoolId(schoolId) {
  const normalized = normalizeSchoolId(schoolId);
  adminSchoolId.value = normalized;
  try {
    window.sessionStorage.setItem(ADMIN_SCHOOL_STORAGE_KEY, normalized);
  } catch {
    // Session storage unavailable; the ref still works for this page load.
  }
  return normalized;
}

// Axios instance for all admin dashboard calls. Injects the bearer token and
// the selected school on every request; the backend only honors the schoolId
// override for superadmins, so sending it unconditionally is harmless.
export const adminApi = axios.create({ baseURL: BACKEND_BASE_URL });

adminApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.params = { ...(config.params || {}), schoolId: adminSchoolId.value };
  return config;
});

// Role state, fetched once per token from GET /username and shared by the
// router guard, App.vue, and the navbar.
const adminRoles = ref({ isAdmin: false, isSuperAdmin: false, loaded: false });

let rolesPromise = null;
let rolesPromiseToken = null;

export function fetchAdminRoles({ force = false } = {}) {
  const token = getStoredToken();
  if (!token) {
    adminRoles.value = { isAdmin: false, isSuperAdmin: false, loaded: true };
    rolesPromise = null;
    rolesPromiseToken = null;
    return Promise.resolve(adminRoles.value);
  }

  if (!force && rolesPromise && rolesPromiseToken === token) {
    return rolesPromise;
  }

  rolesPromiseToken = token;
  rolesPromise = axios
    .get(`${BACKEND_BASE_URL}/username`, { headers: { Authorization: `Bearer ${token}` } })
    .then(({ data }) => {
      adminRoles.value = {
        isAdmin: data?.is_admin === true,
        isSuperAdmin: data?.is_superadmin === true,
        loaded: true,
      };
      return adminRoles.value;
    })
    .catch((error) => {
      console.error('Failed to fetch admin roles:', error);
      adminRoles.value = { isAdmin: false, isSuperAdmin: false, loaded: true };
      rolesPromise = null;
      rolesPromiseToken = null;
      return adminRoles.value;
    });

  return rolesPromise;
}

export function useAdminContext() {
  return {
    adminSchoolId: readonly(adminSchoolId),
    setAdminSchoolId,
    adminRoles: readonly(adminRoles),
    fetchAdminRoles,
  };
}
